import './Breadcrumbs.scss'
import { Fragment } from 'react'
import { Link, useMatches, type UIMatch } from 'react-router-dom'
import { clx } from '../../ui'

/**
 * Route `handle` shape for the integrator's route table (SPEC §4):
 * `{ path: ':id/show', handle: { crumb: (m) => m.params.id ?? null } }`.
 * Return null to contribute no crumb. Needs a data router
 * (createBrowserRouter), as `useMatches` does.
 */
export interface CrumbHandle {
  /**
   * A label for this match, or a whole trail of crumbs (for routes that live
   * under another area's URL, e.g. a profile under its tenant).
   */
  crumb: (match: UIMatch) => string | null | Crumb[]
}

export interface Crumb {
  pathname: string
  label: string
}

function hasCrumb(handle: unknown): handle is CrumbHandle {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    typeof (handle as Partial<CrumbHandle>).crumb === 'function'
  )
}

/** Crumbs from the matched routes' handles. A layout and its index route share a pathname: the deeper crumb wins. */
export function crumbsFrom(matches: UIMatch[]): Crumb[] {
  const crumbs: Crumb[] = []
  for (const match of matches) {
    if (!hasCrumb(match.handle)) continue
    const result = match.handle.crumb(match)
    if (!result) continue
    const items =
      typeof result === 'string' ? [{ pathname: match.pathname, label: result }] : result
    for (const crumb of items) {
      if (crumbs.at(-1)?.pathname === crumb.pathname) crumbs[crumbs.length - 1] = crumb
      else crumbs.push(crumb)
    }
  }
  return crumbs
}

/**
 * `nav aria-label="Breadcrumb"` with an `ol`; the last crumb is the current
 * page. Below `phablet` the middle crumbs collapse into "…" (CSS only). A
 * single crumb only repeats the page title: it is hidden but keeps its
 * space, so the title sits at the same height on every page.
 */
export function Breadcrumbs({ className }: { className?: string }) {
  const crumbs = crumbsFrom(useMatches())
  if (crumbs.length === 0) return null
  const last = crumbs.length - 1

  return (
    <nav
      aria-label="Breadcrumb"
      className={clx('breadcrumbs', crumbs.length === 1 && 'breadcrumbs--is-single', className)}
    >
      <ol className="breadcrumbs__list">
        {crumbs.map((crumb, index) => (
          <Fragment key={crumb.pathname}>
            {index === 1 && last > 1 && (
              <li className="breadcrumbs__item breadcrumbs__ellipsis" aria-hidden="true">
                …
              </li>
            )}
            <li
              className={clx(
                'breadcrumbs__item',
                index > 0 && index < last && 'breadcrumbs__item--is-middle'
              )}
            >
              {index === last ? (
                <span aria-current="page" className="breadcrumbs__current">
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.pathname} className="breadcrumbs__link">
                  {crumb.label}
                </Link>
              )}
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  )
}
